import { Injectable, inject } from '@angular/core';
import { LoggerService } from '../../logging/logger.service';

import {
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
  signOut
} from 'aws-amplify/auth';

import { formatUserNameFromEmail } from '../../../utils/formatUserNameFromEmail';

export interface AuthUser {
  username: string;
  role: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly logger = inject(LoggerService);
  /**
   * Loads the current authenticated user's information from AWS Amplify.
   * If the user is not authenticated, it returns null.
   * @returns Promise<AuthUser | null> - The authenticated user's information or null if not authenticated.
   */
  async loadUser(): Promise<AuthUser | null> {
    this.logger.debug('AuthService', 'Loading authentication session');
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken;

    if (!accessToken) {
      this.logger.debug('AuthService', 'No authenticated session available');
      return null;
    }

    const  { username } = await getCurrentUser();
    const role = session.tokens?.idToken?.payload['profile'] as string;

    this.logger.debug('AuthService', 'Authenticated user resolved');
    return { username: formatUserNameFromEmail(username), role: role ?? null };
  }

  /**
   * Initiates the login process using AWS Amplify's signInWithRedirect method with a custom provider.
   * This method redirects the user to the authentication provider's login page.
   * @returns Promise<void> - A promise that resolves when the login process is initiated.
   */
  async login(): Promise<void> {
    this.logger.info('AuthService', 'Login requested');
    const session = await fetchAuthSession();
    if (session.tokens?.accessToken) {
      this.logger.debug('AuthService', 'Login skipped: session already authenticated');
      return;
    }

    try {
      this.logger.info('AuthService', 'Redirecting to identity provider');
      await signInWithRedirect({provider: {custom: 'DraexlmaierAzureAD'}});
    } catch (error) {
      this.logger.error('AuthService', 'Identity provider redirect failed', error);
    }
  }

  /**
   * Logs out the current authenticated user using AWS Amplify's signOut method.
   * This method clears the user's session and redirects them to the logout page.
   * @returns Promise<void> - A promise that resolves when the logout process is completed.
   */
  async logout(): Promise<void> {
    this.logger.info('AuthService', 'Global sign-out requested');
    try {
      await signOut({ global: true });
      this.logger.info('AuthService', 'Global sign-out completed');
    } catch (error) {
      this.logger.error('AuthService', 'Global sign-out failed', error);
      throw error;
    }
  }
}